import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useLocalePath } from "@/lib/locale-path"
import { trackConversion } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"

// Weekly-article newsletter signup (double opt-in — the server emails a
// confirmation link). Success is tracked locally via onSuccess rather than
// flash so it never trips the EnquiryFormSection's flash-driven thank-you
// state on pages that render both; errors use the newsletter_email key for
// the same reason.
export function NewsletterSignup({ variant = "card" }: { variant?: "card" | "footer" }) {
  const { props } = usePage<PageProps>()
  const t = props.translations.newsletter.signup
  const lp = useLocalePath()

  const [email, setEmail] = React.useState("")
  const [processing, setProcessing] = React.useState(false)
  const [done, setDone] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    setError(null)
    router.post(
      "/newsletter-subscriptions",
      { newsletter_subscriber: { email } },
      {
        preserveScroll: true,
        onSuccess: (page) => {
          const message = (page.props.errors as Record<string, string> | undefined)?.newsletter_email
          if (message) {
            setError(message)
          } else {
            setDone(true)
            setEmail("")
            trackConversion("newsletter_signup")
          }
        },
        onFinish: () => setProcessing(false),
      }
    )
  }

  const inputId = `newsletter-email-${variant}`
  const dark = variant === "footer"

  if (done) {
    return (
      <div className={dark ? "text-white/70" : "rounded-2xl bg-stone p-8 ring-1 ring-hairline"}>
        <p className={dark ? "font-medium text-white/90" : "font-medium text-ink-display"}>
          {t.success_heading}
        </p>
        <p className={`mt-1 text-sm ${dark ? "text-white/60" : "text-ink-muted"}`}>{t.success_body}</p>
      </div>
    )
  }

  if (dark) {
    return (
      <div>
        <h6 className="mb-2 text-xs font-semibold uppercase tracking-widest text-white/40">
          {t.heading}
        </h6>
        <p className="text-sm text-white/60">{t.blurb}</p>
        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-2 sm:flex-row">
          <label htmlFor={inputId} className="sr-only">
            {t.email_label}
          </label>
          <Input
            id={inputId}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.email_placeholder}
            aria-invalid={!!error}
            className="border-white/20 bg-white/10 text-white placeholder:text-white/40"
          />
          <Button type="submit" variant="primary" disabled={processing} className="shrink-0">
            {processing ? t.submitting : t.submit}
          </Button>
        </form>
        {error && <p className="mt-2 text-xs text-red-300">{error}</p>}
        <p className="mt-3 text-xs text-white/40">
          {t.privacy_note}{" "}
          <a href={lp("/privacy-policy")} className="text-white/40 underline hover:text-white/70">
            {t.privacy_link}
          </a>
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-2xl bg-stone p-8 ring-1 ring-hairline">
      <h3>{t.heading}</h3>
      <p className="mt-2 text-sm text-ink-muted">{t.blurb}</p>
      <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <label htmlFor={inputId} className="sr-only">
          {t.email_label}
        </label>
        <Input
          id={inputId}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.email_placeholder}
          aria-invalid={!!error}
        />
        <Button type="submit" variant="primary" disabled={processing} className="shrink-0">
          {processing ? t.submitting : t.submit}
        </Button>
      </form>
      {error && <p className="mt-2 text-xs text-danger-display">{error}</p>}
      <p className="mt-3 text-xs text-ink-muted">
        {t.privacy_note}{" "}
        <a href={lp("/privacy-policy")}>{t.privacy_link}</a>
      </p>
    </div>
  )
}
